import { toCsvLine, downloadCsvBlob } from '../lib/csvUtils';
import type { FulltimePayroll, ParttimePayroll } from './PayrollTypes';
import { minutesToHoursMinutes } from './PayrollTypes';
import { getBenefitLabels, PART_SPECIAL_ALLOWANCE_LABEL } from './PayrollBenefitRules';

// After the part special allowance rule both categories pay a single allowance column.
const ALL_CATEGORIES_SINGLE_ALLOWANCE_LABEL = `特別手当・${PART_SPECIAL_ALLOWANCE_LABEL}`;

/**
 * Export fulltime payroll data as CSV
 */
export function exportFulltimePayrollCsv(
  data: FulltimePayroll[],
  periodKey: string,
  benefitRuleEnabled = false
): void {
  const headerColumns = [
    '社員コード', '氏名', '出勤日数', '休出日数', '休出時間', '有給日数', '欠勤日数',
    '遅刻早退時間', '勤務時間', '普残時間', '夜勤~4回', '5回目~', '夜勤合計',
    '出勤回数',
  ];
  if (!benefitRuleEnabled) headerColumns.push('特別給付金');
  headerColumns.push('特別手当', '交通費', '控除食事代');
  const header = toCsvLine(headerColumns);

  const rows = data.map(row => {
    const columns = [
    row.employeeCode,
    row.employeeName,
    row.attendanceDays,
    row.holidayWorkDays,
    minutesToHoursMinutes(row.holidayWorkMinutes),
    row.paidLeaveDays,
    row.absenceDays,
    minutesToHoursMinutes(row.lateEarlyMinutes),
    minutesToHoursMinutes(row.totalWorkMinutes - row.overtimeMinutes),
    minutesToHoursMinutes(row.overtimeMinutes),
    row.nightShiftUpTo4,
    row.nightShiftOver4,
    row.nightShiftTotal,
    row.attendanceCount,
    ];
    if (!benefitRuleEnabled) columns.push(row.specialBenefit);
    columns.push(
    row.specialAllowance,
    row.transportAllowance,
    row.mealDeduction
    );
    return toCsvLine(columns);
  });

  const csvContent = [header, ...rows].join('\r\n');
  downloadCsvBlob(csvContent, `給与計算_正職員_${periodKey}.csv`);
}

/**
 * Export parttime payroll data as CSV
 */
export function exportParttimePayrollCsv(
  data: ParttimePayroll[],
  periodKey: string,
  benefitRuleEnabled = false,
  partSpecialAllowanceEnabled = false
): void {
  const labels = getBenefitLabels(benefitRuleEnabled);
  const header = toCsvLine([
    '社員コード', '氏名', '出勤日数', '休出日数', '休出時間', '有給日数', '欠勤日数',
    '遅刻早退時間', '勤務時間', '残業時間',
    '出勤回数',
    ...(partSpecialAllowanceEnabled
      ? [PART_SPECIAL_ALLOWANCE_LABEL]
      : benefitRuleEnabled
        ? [labels.allowanceLabel, labels.benefitLabel]
        : [labels.benefitLabel, labels.allowanceLabel]),
    '交通費', '控除食事代'
  ]);

  const rows = data.map(row => {
    const benefitColumns = partSpecialAllowanceEnabled
      ? [row.specialAllowance]
      : benefitRuleEnabled
        ? [row.specialAllowance, row.specialBenefit]
        : [row.specialBenefit, row.specialAllowance];
    return toCsvLine([
    row.employeeCode,
    row.employeeName,
    row.attendanceDays,
    row.holidayWorkDays,
    minutesToHoursMinutes(row.holidayWorkMinutes),
    row.paidLeaveDays,
    row.absenceDays,
    minutesToHoursMinutes(row.lateEarlyMinutes),
    minutesToHoursMinutes(row.totalWorkMinutes - row.overtimeMinutes),
    minutesToHoursMinutes(row.overtimeMinutes),
    row.attendanceTotal,
    ...benefitColumns,
    row.transportAllowance,
    row.mealDeduction
    ]);
  });

  const csvContent = [header, ...rows].join('\r\n');
  downloadCsvBlob(csvContent, `給与計算_非常勤_${periodKey}.csv`);
}

/**
 * Export all employees (fulltime + parttime) as a single CSV
 */
export function exportAllPayrollCsv(
  fulltimeData: FulltimePayroll[],
  parttimeData: ParttimePayroll[],
  periodKey: string,
  benefitRuleEnabled = false,
  partSpecialAllowanceEnabled = false
): void {
  const labels = getBenefitLabels(benefitRuleEnabled);
  const header = toCsvLine([
    '区分', '社員コード', '氏名', '出勤日数', '休出日数', '休出時間', '有給日数',
    '欠勤日数', '遅刻早退時間', '勤務時間', '普残/残業時間',
    '夜勤~4回', '5回目~', '夜勤合計', '出勤回数',
    ...(partSpecialAllowanceEnabled
      ? [ALL_CATEGORIES_SINGLE_ALLOWANCE_LABEL]
      : benefitRuleEnabled
        ? [labels.allowanceLabel, labels.benefitLabel]
        : [labels.benefitLabel, labels.allowanceLabel]),
    '交通費', '控除食事代'
  ]);

  const fulltimeRows = fulltimeData.map(row => {
    const benefitColumns = partSpecialAllowanceEnabled
      ? [row.specialAllowance]
      : benefitRuleEnabled
        ? [row.specialAllowance, row.specialBenefit]
        : [row.specialBenefit, row.specialAllowance];
    return toCsvLine([
    '正職員',
    row.employeeCode,
    row.employeeName,
    row.attendanceDays,
    row.holidayWorkDays,
    minutesToHoursMinutes(row.holidayWorkMinutes),
    row.paidLeaveDays,
    row.absenceDays,
    minutesToHoursMinutes(row.lateEarlyMinutes),
    minutesToHoursMinutes(row.totalWorkMinutes - row.overtimeMinutes),
    minutesToHoursMinutes(row.overtimeMinutes),
    row.nightShiftUpTo4,
    row.nightShiftOver4,
    row.nightShiftTotal,
    row.attendanceCount,
    ...benefitColumns,
    row.transportAllowance,
    row.mealDeduction
    ]);
  });

  const parttimeRows = parttimeData.map(row => {
    const benefitColumns = partSpecialAllowanceEnabled
      ? [row.specialAllowance]
      : benefitRuleEnabled
        ? [row.specialAllowance, row.specialBenefit]
        : [row.specialBenefit, row.specialAllowance];
    return toCsvLine([
    '非常勤',
    row.employeeCode,
    row.employeeName,
    row.attendanceDays,
    row.holidayWorkDays,
    minutesToHoursMinutes(row.holidayWorkMinutes),
    row.paidLeaveDays,
    row.absenceDays,
    minutesToHoursMinutes(row.lateEarlyMinutes),
    minutesToHoursMinutes(row.totalWorkMinutes - row.overtimeMinutes),
    minutesToHoursMinutes(row.overtimeMinutes),
    '',
    '',
    '',
    row.attendanceTotal,
    ...benefitColumns,
    row.transportAllowance,
    row.mealDeduction
    ]);
  });

  const csvContent = [header, ...fulltimeRows, ...parttimeRows].join('\r\n');
  downloadCsvBlob(csvContent, `給与計算_全従業員_${periodKey}.csv`);
}
